import { useEffect, useMemo } from 'react';

import type { AffiliateCampaign } from '../@types/api';
import { useCampaignDetailsQuery } from '../queries/campaigns';

const UNAVAILABLE_CAMPAIGN_STATUS_CODES = new Set([403, 404]);

/**
 * Resolves a URL-backed campaign ID into an autocomplete option.
 * Player filtering does not wait for this lookup; it only restores the visible campaign label.
 */
const useCampaignOptionHydration = (campaignId: string | null, onUnavailable: () => void): AffiliateCampaign[] => {
  const { data: campaign, error, isError, isSuccess } = useCampaignDetailsQuery(campaignId);
  const errorStatusCode = error?.response?.data?.statusCode;
  const campaignIsUnavailable =
    (isSuccess && campaign === null) ||
    (isError && errorStatusCode !== undefined && UNAVAILABLE_CAMPAIGN_STATUS_CODES.has(errorStatusCode));

  useEffect(() => {
    // Preserve the URL filter on transient failures; only a definitive response may remove it.
    if (campaignId && campaignIsUnavailable) onUnavailable();
  }, [campaignId, campaignIsUnavailable, onUnavailable]);

  return useMemo(() => (campaign ? [campaign] : []), [campaign]);
};

export default useCampaignOptionHydration;
